import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ExternalLink } from 'lucide-react';
import type { WeldAnalyticsBundle, WeldInsightDrillDown } from '../types/weld';
import { generateWeldInsights, INSIGHT_CATEGORIES } from '../utils/generateWeldInsights';
import { isWeldIqAnalyticsEmpty } from '../utils/weldIqEmptyState';
import { glossaryText } from '../utils/weldFieldGlossary';
import { ColumnHeaderHelp } from './shared/ColumnHeaderHelp';
import { InsightsSectionNav } from './shared/InsightsSectionNav';
import type { PendingWeldDrillDown } from './dashboard/drilldown/drillDownTypes';

interface WeldInsightsPanelProps {
  bundle: WeldAnalyticsBundle | null;
  onOpenWeldDrillDown?: (target: PendingWeldDrillDown) => void;
}

const priClass: Record<string, string> = {
  HIGH: 'text-readable-red bg-hero-red-10 border-hero-red-20',
  MEDIUM: 'text-[#B38600] bg-[rgba(230,161,0,0.08)] border-[rgba(230,161,0,0.25)]',
  LOW: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)] border-[#E2E5EA]',
};

function drillTargetId(drillDown: WeldInsightDrillDown): string | null {
  if (drillDown.kind === 'weld') return drillDown.weldNumber ?? null;
  return drillDown.jobNumber || null;
}

function drillLinkAriaLabel(drillDown: WeldInsightDrillDown): string {
  if (drillDown.kind === 'weld' && drillDown.weldNumber) {
    return `Open drill-down for weld ${drillDown.weldNumber}`;
  }
  return `Open drill-down for job ${drillDown.jobNumber}`;
}

/** Split text and wrap the first occurrence of `id` as an inline drill-down link. */
function LinkedInsightText({
  text,
  drillDown,
  onOpen,
  className,
  as: Tag = 'span',
}: {
  text: string;
  drillDown?: WeldInsightDrillDown;
  onOpen?: () => void;
  className?: string;
  as?: 'span' | 'h4' | 'p';
}) {
  const id = drillDown ? drillTargetId(drillDown) : null;
  if (!id || !onOpen || !text.includes(id)) {
    return <Tag className={className}>{text}</Tag>;
  }

  const idx = text.indexOf(id);
  const before = text.slice(0, idx);
  const after = text.slice(idx + id.length);

  return (
    <Tag className={className}>
      {before}
      <button
        type="button"
        onClick={onOpen}
        aria-label={drillLinkAriaLabel(drillDown!)}
        className="inline-flex items-center gap-0.5 font-semibold text-readable-red underline decoration-readable-red/40 underline-offset-2 hover:decoration-readable-red"
      >
        {id}
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {after}
    </Tag>
  );
}

export const WeldInsightsPanel = ({ bundle, onOpenWeldDrillDown }: WeldInsightsPanelProps) => {
  const insights = useMemo(() => generateWeldInsights(bundle), [bundle]);
  const [category, setCategory] = useState<string>(INSIGHT_CATEGORIES[0]);
  const analyticsEmpty = isWeldIqAnalyticsEmpty(bundle);

  const filtered = insights.filter((i) => i.category === category);

  return (
    <div className="space-y-6">
      {!analyticsEmpty && (
        <InsightsSectionNav
          aria-label="Weld insights sections"
          tabs={INSIGHT_CATEGORIES.map((c) => ({
            id: c,
            label: c,
            count: insights.filter((i) => i.category === c).length,
          })).filter((t) => (t.count ?? 0) > 0)}
          activeId={category}
          onChange={setCategory}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {filtered.map((ins, idx) => {
          const canDrill = Boolean(ins.drillDown && onOpenWeldDrillDown);
          const openDrill = () => {
            if (!ins.drillDown || !onOpenWeldDrillDown) return;
            if (ins.drillDown.kind === 'weld' && ins.drillDown.weldNumber) {
              onOpenWeldDrillDown({
                jobNumber: ins.drillDown.jobNumber,
                weldNumber: ins.drillDown.weldNumber,
                scope: 'single',
                variant: 'compliance',
              });
              return;
            }
            if (ins.drillDown.kind === 'job') {
              onOpenWeldDrillDown({
                jobNumber: ins.drillDown.jobNumber,
                scope: 'job',
                variant: 'compliance',
              });
            }
          };

          const linkProps = canDrill
            ? { drillDown: ins.drillDown, onOpen: openDrill }
            : {};

          return (
            <article
              key={`${ins.title}-${idx}`}
              className="rounded-2xl border border-[#E2E5EA] bg-white p-4 shadow-sm"
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span
                  className={clsx(
                    'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                    priClass[ins.priority] || priClass.LOW,
                  )}
                >
                  <ColumnHeaderHelp label={ins.priority} help={glossaryText('priority')} />
                </span>
                <span className="text-xs text-heritage-grey">
                  <ColumnHeaderHelp
                    label={`${ins.confidence}% confidence`}
                    help={glossaryText('confidence')}
                  />
                </span>
                <span className="text-xs text-heritage-grey">
                  <ColumnHeaderHelp label={ins.category} help={glossaryText('category')} />
                </span>
              </div>
              <LinkedInsightText
                as="h4"
                text={ins.title}
                className="font-semibold text-blueprint-blue"
                {...linkProps}
              />
              <LinkedInsightText
                as="p"
                text={ins.body}
                className="mt-2 text-sm text-text-grey"
                {...linkProps}
              />
              <details className="mt-3 group">
                <summary className="cursor-pointer text-xs font-medium text-heritage-grey hover:text-blueprint-blue">
                  Why this insight?
                </summary>
                <p className="mt-2 text-xs leading-relaxed text-heritage-grey">{ins.why}</p>
              </details>
              <div className="mt-4 rounded-lg border border-hero-red-20 bg-hero-red-10/40 px-3 py-2.5">
                <p className="text-sm text-blueprint-blue">
                  <ColumnHeaderHelp
                    label="Recommendation"
                    help={glossaryText('recommendation')}
                    className="font-semibold"
                  />
                  : {ins.recommendation}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      {filtered.length === 0 && !analyticsEmpty && (
        <p className="rounded-xl border border-dashed border-[#E2E5EA] p-8 text-center text-heritage-grey">
          No insights in this category for the current filters.
        </p>
      )}
      {analyticsEmpty && (
        <p className="rounded-xl border border-dashed border-[#E2E5EA] p-8 text-center text-heritage-grey">
          No insights available for the selected jobs yet.
        </p>
      )}
    </div>
  );
};
